import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Input from './inputs';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { elRed } from 'src/styles/colors';

export const SELECT_OPTIONS = gql`
  query SelectOptions {
    selectOptions {
      assetCategories
      assetMakes
      assetModels
    }
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FilterFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const MatchCount = styled.div`
  font-weight: 600;
`;

const CountGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
  gap: 6px 16px;
  align-items: end;
`;

const AdvancedToggle = styled.button`
  padding: 0;
  background: none;
  border: none;
  color: ${elRed};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
`;

const RangeField = styled.div`
  grid-column: span 2;
`;

const RangeRow = styled.div`
  display: flex;
  gap: 10px;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

const StateField = styled.div`
  grid-column: span 2;
`;

const Filter = ({
  filterType, queryVariables, setQueryVariables, setFilterChange,
  setInventoryFilterValues, matchCount, shownCount, headerAction
}) => {

  const secondaryColumns = ['milesMin', 'milesMax', 'stockNumber', 'vin', 'state'];
  const hasSecondaryFilter = (queryVariables?.filterColumns || [])
    .some(column => secondaryColumns.includes(column));
  const [showAdvanced, setShowAdvanced] = useState(hasSecondaryFilter);

  const { data } = useQuery(SELECT_OPTIONS);

  const {
    control, watch, setValue, formState: { errors }
  } = useForm({
    defaultValues: defaultValues()
  });


  watch(data => { updateFilter(data); });
  const watchState = watch('state');
  const watchYearMin = watch('yearMin');
  const watchYearMax = watch('yearMax');

  return (
    <>
      {getInputs(filterType)}
    </>
  )

  function getDefaultValues(column) {
    let columns = queryVariables?.filterColumns || [];
    let names = queryVariables?.filterNames || [];
    let colIndex = columns.indexOf(column);
    let value = undefined;
    if(colIndex >= -1) {
      value = names[colIndex];
    }

    if(column === 'category' && value === 'Tractor') {
      value = 'Semi-Truck'
    }
    if(column === 'state' && value?.length) {
      value = [...value];
    }
    if(column === 'category' && !value) {
      colIndex = columns.indexOf('subCategory');
      if(colIndex !== -1) {
        value = ` - ${names[colIndex] === 'Non-Sleeper' ? 'Day Cab' : names[colIndex]}`;
      }
    }

    return value !== undefined ? value : (column === 'state' ? [] : '')
  }

  function defaultValues() {
    if(filterType === 'Asset') {
      return (
        {
          category: getDefaultValues('category'),
          yearMin: getDefaultValues('yearMin'),
          yearMax: getDefaultValues('yearMax'),
          make: getDefaultValues('make'),
          model: getDefaultValues('model'),
          milesMin: getDefaultValues('milesMin'),
          milesMax: getDefaultValues('milesMax'),
          stockNumber: getDefaultValues('stockNumber'),
          vin: getDefaultValues('vin'),
          subCategory: getDefaultValues('subCategory'),
          state: getDefaultValues('state')
        }
      )
    }
  }

  function getInputs() {
    let assetCategories = data?.selectOptions.assetCategories || [];
    assetCategories = assetCategories.filter(category => (
      !['TRACTOR', 'TRAILER'].includes(category.toUpperCase())
    ));

    assetCategories = [
      'Semi-Truck', ' - Day Cab', ' - Sleeper', 'Trailer', ...assetCategories
    ];

    if(filterType === 'Asset') {
      return (
        <FilterContainer id="filter-container">
          <FieldGrid>
            <Input.SearchablePicker
              label="Category:"
              name="category"
              control={control}
              errors={errors}
              options={assetCategories.map(option => {
                return {value: option, label: option}
              })}
            />
            <Input.SearchablePicker
              label="Make:"
              name="make"
              control={control}
              errors={errors}
              options={data?.selectOptions.assetMakes.map(option => {
                return {value: option, label: option}
              })}
            />
            <Input.SearchablePicker
              label="Model:"
              name="model"
              control={control}
              errors={errors}
              options={data?.selectOptions.assetModels.map(option => {
                return {value: option, label: option}
              })}
            />
            <RangeField>
              <RangeRow>
                <Input.YearPicker
                  label="Min Year:"
                  name="yearMin"
                  control={control}
                  errors={errors}
                  watchMax={watchYearMax}
                />
                <Input.YearPicker
                  label="Max Year:"
                  name="yearMax"
                  control={control}
                  errors={errors}
                  watchMin={watchYearMin}
                />
              </RangeRow>
            </RangeField>
          </FieldGrid>
          {showAdvanced &&
            <FieldGrid style={{marginTop: '8px'}}>
              <RangeField>
                <RangeRow>
                  <Input.Number
                    label="Min Miles:"
                    name="milesMin"
                    control={control}
                    errors={errors}
                  />
                  <Input.Number
                    label="Max Miles:"
                    name="milesMax"
                    control={control}
                    errors={errors}
                  />
                </RangeRow>
              </RangeField>
              <Input.Text
                label="Stock Number:"
                name="stockNumber"
                control={control}
                errors={errors}
              />
              <Input.Text
                label="VIN:"
                name="vin"
                control={control}
                errors={errors}
              />
              <StateField>
                <Input.StateMultiSelect
                  label="State:"
                  name="state"
                  value={watchState}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  defaultValues={getDefaultValues('state')}
                />
              </StateField>
            </FieldGrid>
          }
          <FilterFooter>
            <AdvancedToggle
              type="button"
              onClick={() => setShowAdvanced(value => !value)}
            >
              {showAdvanced ? '− Hide advanced options' : '+ Advanced search options'}
            </AdvancedToggle>
            {matchCount != null &&
              <CountGroup>
                <MatchCount>{`Showing ${shownCount} results of ${matchCount}`}</MatchCount>
                {headerAction}
              </CountGroup>
            }
          </FilterFooter>
        </FilterContainer>
      )
    }
  }

  function updateFilter(watchAllFields) {
    if(filterType === 'Asset') {
      let filterColumns = [];
      let filterNames = [];
      let keys = Object.keys(watchAllFields);

      keys.map(key => {
        if ('category' === key && watchAllFields[key] !== "") {
          if ('Semi-Truck' === watchAllFields[key]) {
            filterColumns.push(key);
            filterNames.push('Tractor')
          } else if (' - Day Cab' === watchAllFields[key]) {
            filterColumns.push('subCategory');
            filterNames.push('Non-Sleeper');
          } else if (' - Sleeper' === watchAllFields[key]) {
            filterColumns.push('subCategory');
            filterNames.push('Sleeper');
          } else {
            filterColumns.push(key);
            filterNames.push(watchAllFields[key])
          }
        }else {
          if(watchAllFields[key] !== "") {
            filterColumns.push(key);
            filterNames.push(watchAllFields[key])
          }
        }
      })

      let newQueryVariables = {
        ...queryVariables,
        filterColumns: filterColumns,
        filterNames: filterNames
      }
      setFilterChange(true);
      setInventoryFilterValues(newQueryVariables);
      setQueryVariables(newQueryVariables);
    }
  }
}

export default Filter;
